import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { DashboardLayout } from '../common/page_component/index.js';
import Loader from '../common/Loader';
import Alert from '../common/Alert.js';
import routeService from '../../services/RouteService.js';
import { Route as RouteIcon, Pencil, ArrowLeft } from 'lucide-react';
import Button from '../common/Button.js';
import { formatLocationDropdownLabel, sortLocationsByNumber } from '../../utils/locationDropdownLabel.js';
import companySettingsService from '../../services/companySettingsService.js';
import { isLrShipmentFormat } from '../../utils/shipmentIdFormat.js';

const readOnlyClass =
  'w-full min-w-0 px-3 sm:px-4 py-2.5 sm:py-2 border border-gray-200 rounded-xl text-sm bg-gray-50 text-gray-800 break-words';

const ViewRoute = () => {
  const { routeId } = useParams();
  const navigate = useNavigate();
  const [route, setRoute] = useState(null);
  const [loading, setLoading] = useState(true);
  const [useNumberCode, setUseNumberCode] = useState(false);

  useEffect(() => {
    const fetchRoute = async () => {
      try {
        const response = await routeService.getById(routeId);
        setRoute(response.data || response);
      } catch (err) {
        Alert.error('Failed to load route details');
        console.error(err);
        navigate('/routes');
      } finally {
        setLoading(false);
      }
    };
    fetchRoute();
  }, [routeId, navigate]);

  useEffect(() => {
    let cancelled = false;
    companySettingsService.getSettings()
      .then((res) => {
        if (cancelled) return;
        setUseNumberCode(
          !!res?.data?.lr_branch_code_required
          || isLrShipmentFormat(res?.data?.shipment_id_format),
        );
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);

  const label = (hub) => hub?.route_label || formatLocationDropdownLabel(hub, useNumberCode) || '—';

  const chips = (hubs) => {
    const list = sortLocationsByNumber(hubs || []);
    if (!list.length) return <p className="text-[11px] sm:text-xs text-gray-500">None</p>;
    return (
      <div className="flex flex-wrap gap-1.5">
        {list.map((hub) => (
          <span
            key={hub.id}
            className="inline-flex items-center px-2 py-0.5 rounded-lg text-xs font-medium bg-blue-50 text-blue-800 border border-blue-200"
          >
            {label(hub)}
          </span>
        ))}
      </div>
    );
  };

  const pathPreview = () => {
    if (!route) return '—';
    const names = (hubs) => (hubs || []).map((hub) => hub.location).filter(Boolean);
    const chunks = [];
    const sources = names(route.source_locations?.length ? route.source_locations : (route.from_location ? [route.from_location] : []));
    const vias = names(route.via_locations);
    const targets = names(route.target_locations?.length ? route.target_locations : (route.to_location ? [route.to_location] : []));
    if (sources.length) chunks.push(sources.join(', '));
    if (vias.length) chunks.push(vias.join(' → '));
    if (targets.length) chunks.push(targets.join(', '));
    return chunks.join(' → ') || '—';
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center py-20">
          <Loader fullScreen={false} size="default" message="Loading route details..." />
        </div>
      </DashboardLayout>
    );
  }

  if (!route) {
    return (
      <DashboardLayout>
        <div className="w-full min-w-0 max-w-full mx-auto">
          <p className="text-gray-600">Route not found</p>
        </div>
      </DashboardLayout>
    );
  }

  const sources = route.source_locations?.length
    ? route.source_locations
    : (route.from_location ? [route.from_location] : []);
  const targets = route.target_locations?.length
    ? route.target_locations
    : (route.to_location ? [route.to_location] : []);
  const vias = route.via_locations || [];
  const crossings = route.crossing_points || [];

  return (
    <DashboardLayout>
      <div className="w-full min-w-0 max-w-full mx-auto">
        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3 sm:gap-4 mb-4 sm:mb-6">
          <h1 className="text-2xl sm:text-xl font-bold text-secondary flex items-center gap-2 sm:gap-3">
            <RouteIcon className="w-6 h-6 text-secondary shrink-0" />
            {route.name || 'Route'}
          </h1>
          <button
            onClick={() => navigate('/routes')}
            className="group flex items-center justify-center gap-2 px-3 sm:px-3 py-2.5 sm:py-2 text-xs sm:text-sm text-buttonBg bg-buttonTxt hover:text-buttonTxt hover:bg-buttonBg border border-current rounded-xl transition-all duration-300 font-medium whitespace-nowrap shrink-0"
          >
            <span className="group-hover:-translate-x-1 transition-transform"><ArrowLeft className="w-4 h-4" /></span>
            Back to Routes
          </button>
        </div>

        <div className="bg-white rounded-xl shadow-sm p-4 sm:p-8 space-y-6 sm:space-y-8">
          <div>
            <h3 className="text-base sm:text-md font-semibold text-gray-700 mb-3 sm:mb-4 pb-2 border-b-2 border-grey-400">
              Route Information
            </h3>
            <p className="text-xs sm:text-sm text-gray-600 leading-relaxed mb-4 sm:mb-6">
              The service number is the route id. Source and target locations are required.
              Departure, arrival, and crossing locations are optional.
            </p>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4 md:gap-6">
              <div className="min-w-0">
                <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">
                  Service Number
                </label>
                <div className={readOnlyClass}>{route.route_id || '—'}</div>
                <p className="text-[11px] text-gray-500 mt-1">This is the route id used on the dispatch Excel.</p>
              </div>
              <div className="min-w-0">
                <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">
                  Route Name
                </label>
                <div className={readOnlyClass}>{route.name || '—'}</div>
              </div>
              <div className="min-w-0">
                <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">
                  Source Locations
                </label>
                {chips(sources)}
              </div>
              <div className="min-w-0">
                <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">
                  Target Locations
                </label>
                {chips(targets)}
              </div>
              <div className="min-w-0">
                <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">
                  Departure Time
                </label>
                <div className={readOnlyClass}>{route.departure_time || '—'}</div>
              </div>
              <div className="min-w-0">
                <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">
                  Arrival Time
                </label>
                <div className={readOnlyClass}>{route.arrival_time || '—'}</div>
              </div>
            </div>

            <div className="min-w-0 mt-4 sm:mt-6">
              <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">
                Via Stops (travel order)
              </label>
              {vias.length === 0 ? (
                <p className="text-[11px] sm:text-xs text-gray-500">No middle stops.</p>
              ) : (
                <ul className="space-y-1.5 sm:space-y-2">
                  {vias.map((hub, index) => (
                    <li
                      key={hub.id || index}
                      className="flex items-center gap-2 px-2.5 sm:px-3 py-2 border border-gray-200 rounded-xl bg-gray-50 min-w-0"
                    >
                      <span className="text-xs sm:text-sm text-gray-800 truncate min-w-0">
                        {index + 1}. {label(hub)}
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            <div className="min-w-0 mt-4 sm:mt-6">
              <label className="block text-xs sm:text-sm font-medium text-secondary mb-1">
                Crossing Locations
              </label>
              <p className="text-[11px] sm:text-xs text-gray-500 mb-2">
                Optional. Each stop lists the branches whose shipments that location’s agent can see in crossing.
              </p>
              {crossings.length === 0 ? (
                <p className="text-[11px] sm:text-xs text-gray-500">No crossing locations.</p>
              ) : (
                <div className="space-y-3">
                  {crossings.map((point) => (
                    <div key={point.location_hub_id} className="border border-gray-200 rounded-xl p-3 bg-gray-50">
                      <p className="text-sm font-semibold text-gray-800 mb-2">
                        {label(point.location)}
                      </p>
                      {chips(point.branches)}
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="rounded-xl bg-orange-50 border border-orange-100 px-3 sm:px-4 py-2.5 sm:py-3 mt-4">
              <p className="text-[11px] sm:text-xs font-medium text-orange-800 mb-0.5 sm:mb-1">Travel path preview</p>
              <p className="text-xs sm:text-sm text-orange-900 font-semibold break-words">{pathPreview()}</p>
            </div>
          </div>

          <div className="flex flex-row items-center justify-end gap-2 sm:gap-3 pt-1 sm:pt-2">
            <Button type="button" variant="secondary" onClick={() => navigate('/routes')}>
              Back to List
            </Button>
            <Button type="button" variant="primary" icon={Pencil} onClick={() => navigate(`/routes/edit/${route.route_id || routeId}`)}>
              Edit Route
            </Button>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default ViewRoute;
